/**
 * Por que a sessão acabou:
 * - `expired`: o servidor derrubou a sessão no meio do uso (mostra aviso e volta ao ponto onde estava);
 * - `logout`: a própria pessoa saiu (sem aviso, e o próximo login cai em "/").
 */
export type EndReason = 'expired' | 'logout';

export interface AuthFlags {
  endReason: EndReason | null;
  loggingOut: boolean;
  logoutError: string | null;
}

export interface FlagsStore {
  get: () => AuthFlags;
  set: (patch: Partial<AuthFlags>) => void;
  subscribe: (listener: () => void) => () => void;
}

/**
 * Pequeno store fora do React para os sinais que acompanham a sessão.
 *
 * Por que não `useState`: o tratador de 401 precisa gravar o motivo ANTES de a sessão virar null
 * e a tela de entrada precisa enxergar os dois juntos. Com `useSyncExternalStore` as duas
 * leituras são síncronas e consistentes; com `useState` a tela poderia renderizar "sem sessão"
 * ainda sem o motivo e perder o aviso de sessão expirada. Também permite ler o valor atual
 * dentro de callbacks assíncronos (clique duplo no "Sair", 401 que chega durante o logout).
 */
export function createFlagsStore(): FlagsStore {
  let flags: AuthFlags = { endReason: null, loggingOut: false, logoutError: null };
  const listeners = new Set<() => void>();

  return {
    get: () => flags,
    set(patch) {
      const next = { ...flags, ...patch };
      const changed = (Object.keys(next) as (keyof AuthFlags)[]).some((k) => next[k] !== flags[k]);
      if (!changed) return;
      flags = next;
      listeners.forEach((listener) => listener());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
